import React, { useState, useRef } from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Box,
  Stack,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PeopleOutline, StraightenOutlined, LocationOnOutlined, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { getImagePath } from '../utils/imageUtils';

const YachtCard = ({ yacht }) => {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const isSwipingRef = useRef(false);
  const isMouseDown = useRef(false);

  const getImageUrls = () => {
    const imageNames = yacht.imageUrls || yacht.imageNames;
    if (!imageNames || !Array.isArray(imageNames) || imageNames.length === 0) {
      return ['/images/yachts/default-yacht.jpg'];
    }
    return getImagePath(imageNames);
  };

  const images = getImageUrls();
  const hasMultipleImages = images.length > 1;

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    isSwipingRef.current = false;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 10) {
      isSwipingRef.current = true;
    }
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance && isSwipingRef.current) {
      if (diff > 0) {
        // Swipe left - next image
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      } else {
        // Swipe right - previous image
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
    isSwipingRef.current = false;
  };

  const handleMouseDown = (e) => {
    isMouseDown.current = true;
    touchStartX.current = e.clientX;
    isSwipingRef.current = false;
  };

  const handleMouseMove = (e) => {
    if (!isMouseDown.current) return;
    touchEndX.current = e.clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 10) {
      isSwipingRef.current = true;
    }
  };

  const handleMouseUp = () => {
    if (!isMouseDown.current) return;
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance && isSwipingRef.current) {
      if (diff > 0) {
        // Swipe left - next image
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
      } else {
        // Swipe right - previous image
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
    isMouseDown.current = false;
    isSwipingRef.current = false;
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
  };

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handleCardClick = () => {
    if (!isSwipingRef.current) {
      navigate(`/yacht/${yacht.id}`);
    }
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          '& .yacht-image': {
            transform: 'scale(1.05)'
          },
          '& .nav-arrow': {
            opacity: 1
          }
        }
      }}
      onClick={handleCardClick}
    >
      <Box 
        sx={{ 
          position: 'relative', 
          aspectRatio: '4/3',
          overflow: 'hidden', 
          borderRadius: '12px 12px 0 0',
          width: '100%',
          userSelect: 'none'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleImageClick}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            transition: 'transform 0.3s ease',
            transform: `translateX(-${currentImageIndex * 100}%)`
          }}
        >
          {images.map((imageUrl, index) => (
            <CardMedia
              key={index}
              component="img"
              image={imageUrl}
              alt={`${yacht.name} - Image ${index + 1}`}
              className="yacht-image"
              sx={{
                minWidth: '100%',
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                backgroundColor: 'grey.200',
                transition: 'transform 0.3s ease',
                flexShrink: 0
              }}
            />
          ))}
        </Box>
        
        {/* Navigation arrows */}
        {hasMultipleImages && (
          <>
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                color: '#1976d2',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.95)'
                },
                opacity: 0,
                transition: 'opacity 0.3s ease'
              }}
              className="nav-arrow"
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                color: '#1976d2',
                zIndex: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.95)'
                },
                opacity: 0,
                transition: 'opacity 0.3s ease'
              }}
              className="nav-arrow"
            >
              <ChevronRight />
            </IconButton>
          </>
        )}

        {/* Image indicators (dots) */}
        {hasMultipleImages && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 1,
              zIndex: 2
            }}
          >
            {images.map((_, index) => (
              <Box
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImageIndex(index);
                }}
                sx={{
                  width: currentImageIndex === index ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: currentImageIndex === index 
                    ? 'rgba(255, 255, 255, 0.95)' 
                    : 'rgba(255, 255, 255, 0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </Box>
        )}

        {/* Overlay gradient */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '50%',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.3))',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />
      </Box>
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Typography 
          gutterBottom 
          variant="h5" 
          component="h2"
          sx={{
            fontWeight: 600,
            color: '#1976d2',
            mb: 2
          }}
        >
          {yacht.name}
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {yacht.description}
        </Typography>

        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleOutline fontSize="small" />
            <Typography variant="body2">
              {yacht.capacity} osób
            </Typography>
          </Box>
          
          {yacht.length && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StraightenOutlined fontSize="small" />
              <Typography variant="body2">
                {yacht.length} metrów
              </Typography>
            </Box>
          )}
          
          {yacht.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnOutlined fontSize="small" />
              <Typography variant="body2">
                {yacht.location}
              </Typography>
            </Box>
          )}
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 'auto' }}>
          <Button 
            variant="contained" 
            size="medium"
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#1565c0',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            Zobacz Szczegóły
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default YachtCard;
